import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import clinicService from '../../services/clinicService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';

const METHODS = ['wire', 'ach', 'card'];
const validId = (value) => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
const changesFor = (value) => ({
  pricingClinicId: value.pricingClinicId,
  paymentMethods: { ...value.paymentMethods },
});
const same = (a, b) =>
  a.pricingClinicId === b.pricingClinicId &&
  METHODS.every((method) => a.paymentMethods[method] === b.paymentMethods[method]);
const readSettings = (response, id) => {
  const value = response?.data?.data;
  if (
    value?.distributorId !== id ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    value.currency !== 'USD' ||
    (value.pricingClinicId !== null && !validId(value.pricingClinicId)) ||
    METHODS.some((method) => typeof value.paymentMethods?.[method] !== 'boolean') ||
    (value.pricingSource !== null &&
      (value.pricingSource?.id !== value.pricingClinicId ||
        typeof value.pricingSource.name !== 'string' ||
        typeof value.pricingSource.available !== 'boolean'))
  )
    throw new Error('Invalid purchase settings');
  return value;
};
const validRate = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return false;
  const decimal = String(value).match(/^(\d+)(?:\.(\d{1,4}))?$/);
  return Boolean(
    decimal && Number.isSafeInteger(Number(decimal[1] + (decimal[2] || '').padEnd(4, '0')))
  );
};
const eligible = (row) =>
  validId(row?._id) &&
  typeof row.name === 'string' &&
  row.origin === 'quickbooks' &&
  row.isActive === true &&
  !row.removedAt &&
  typeof row.qbRealmId === 'string' &&
  row.qbRealmId.trim() &&
  typeof row.qbCustomerId === 'string' &&
  row.qbCustomerId.trim();

export default function DistributorPurchaseSettings({
  distributor,
  disabled,
  onLockChange,
  workspaceAdmin = false,
}) {
  const t = useT();
  const id = distributor._id;
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(false);
  const session = useRef(captureSession());
  const target = useRef(id);
  const pending = useRef(false);
  const sequence = useRef(0);
  const current = () =>
    mounted.current && target.current === id && isSessionCurrent(session.current);
  const dirty = Boolean(saved && draft && !same(saved, draft));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, []);
  useEffect(() => {
    onLockChange(busy || dirty || uncertain);
    const warn = (event) => {
      if (busy || dirty || uncertain) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      onLockChange(false);
      window.removeEventListener('beforeunload', warn);
    };
  }, [busy, dirty, uncertain, onLockChange]);

  const reload = async () => {
    if (!current() || pending.current || disabled || distributor.isActive !== true) return;
    pending.current = true;
    const generation = ++sequence.current;
    setBusy(true);
    setExpanded(true);
    setError('');
    setNotice('');
    try {
      const [response, options] = await Promise.all([
        distributorService.adminPurchaseSettings(id),
        workspaceAdmin
          ? distributorService.adminPurchasePricingOptions()
          : clinicService.adminList(),
      ]);
      if (!current() || generation !== sequence.current) return;
      const value = readSettings(response, id);
      const rows = options?.data?.data?.data || options?.data?.data;
      if (!Array.isArray(rows)) throw new Error('Invalid pricing accounts');
      setClinics(rows.filter(eligible));
      setSaved(value);
      setDraft(changesFor(value));
      setUncertain(false);
    } catch {
      if (current() && generation === sequence.current)
        setError('Purchase settings could not load. Please retry.');
    } finally {
      pending.current = false;
      if (current() && generation === sequence.current) setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      disabled ||
      distributor.isActive !== true ||
      pending.current ||
      uncertain ||
      !dirty
    )
      return;
    const changes = changesFor(draft);
    if (changes.pricingClinicId && !clinics.some((row) => row._id === changes.pricingClinicId)) {
      setError('Choose an available pricing account, or select Not configured.');
      return;
    }
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const value = readSettings(
        await distributorService.saveAdminPurchaseSettings(id, {
          revision: saved.revision,
          changes,
        }),
        id
      );
      if (!current()) return;
      if (!same(value, changes) || value.revision <= saved.revision)
        throw new Error('Unconfirmed purchase settings');
      setSaved(value);
      setDraft(changesFor(value));
      setNotice('Purchase settings saved.');
    } catch (failure) {
      if (!current()) return;
      setUncertain(true);
      if (failure?.response?.status === 409) {
        setError(
          'Purchase settings changed. Your edits are still here. Discard and reload before saving again.'
        );
      } else {
        try {
          const value = readSettings(await distributorService.adminPurchaseSettings(id), id);
          if (!current()) return;
          if (same(value, changes) && value.revision > saved.revision) {
            setSaved(value);
            setDraft(changesFor(value));
            setUncertain(false);
            setNotice('Purchase settings saved.');
          } else {
            setError(
              'Purchase settings could not be confirmed. Your edits are still here. Discard and reload before saving again.'
            );
          }
        } catch {
          if (current())
            setError(
              'Purchase settings could not be confirmed. Your edits are still here. Discard and reload before saving again.'
            );
        }
      }
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const discard = () => {
    if (!current() || disabled || pending.current) return;
    if (distributor.isActive !== true) {
      setDraft(changesFor(saved));
      setUncertain(false);
      setError('');
      return;
    }
    reload();
  };
  const source =
    draft?.pricingClinicId === saved?.pricingClinicId
      ? saved?.pricingSource
      : clinics.find((row) => row._id === draft?.pricingClinicId);
  const rate = source?.ratePerUnit;
  const available = source?.available !== false && validRate(rate);
  const inputClass = 'mt-1 block w-full rounded border p-2';
  return (
    <section
      aria-label={t('Distributor purchase settings')}
      className="me-distributor-admin-card space-y-4"
    >
      <h2 className="text-lg font-semibold">
        {t('Purchase settings for {name}', { name: distributor.name })}
      </h2>
      <p className="text-sm text-slate-600">
        {t(
          'Use an existing MedEffects client price. This reference does not copy customers, grant client access, or change their price.'
        )}
      </p>
      <p className="text-sm text-slate-600">
        {t(
          'These settings control purchase policy only. They do not enable live payments, charge money, or add bank units.'
        )}
      </p>
      {distributor.isActive !== true && (
        <p role="status">{t('This distributor is inactive. Purchase settings are read-only.')}</p>
      )}
      {!expanded && (
        <button
          type="button"
          disabled={disabled || distributor.isActive !== true}
          onClick={reload}
          className="me-distributor-admin-secondary"
        >
          {t('Configure purchases')}
        </button>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      {busy && <p role="status">{t('Updating purchase settings...')}</p>}
      {expanded && !draft && !busy && (
        <button
          type="button"
          disabled={disabled}
          onClick={reload}
          className="me-distributor-admin-secondary"
        >
          {t('Retry')}
        </button>
      )}
      {draft && (
        <form onSubmit={save} className="space-y-4">
          <fieldset
            disabled={disabled || busy || uncertain || distributor.isActive !== true}
            className="space-y-4"
          >
            <label className="block">
              {t('MedEffects pricing account')}
              <select
                className={inputClass}
                value={draft.pricingClinicId || ''}
                onChange={(event) => {
                  setDraft((value) => ({ ...value, pricingClinicId: event.target.value || null }));
                  setNotice('');
                }}
              >
                <option value="">{t('Not configured')}</option>
                {draft.pricingClinicId &&
                  !clinics.some((row) => row._id === draft.pricingClinicId) && (
                    <option value={draft.pricingClinicId} disabled>
                      {t('Pricing account unavailable')}
                    </option>
                  )}
                {clinics.map((row) => (
                  <option key={row._id} value={row._id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-sm" aria-live="polite">
              {t('Current purchase rate')}:{' '}
              {available
                ? t('{rate} USD per unit', {
                    rate: rate.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    }),
                  })
                : t('Purchase pricing unavailable')}
            </p>
            <p className="text-xs text-slate-600">
              {t(
                'Prices are read-only here. Quotes recheck the current client price before purchase.'
              )}
            </p>
            <fieldset className="flex flex-wrap gap-4">
              <legend className="mb-2 font-medium">{t('Allowed purchase methods')}</legend>
              {METHODS.map((method) => (
                <label key={method} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.paymentMethods[method]}
                    onChange={(event) => {
                      setDraft((value) => ({
                        ...value,
                        paymentMethods: { ...value.paymentMethods, [method]: event.target.checked },
                      }));
                      setNotice('');
                    }}
                  />
                  {t({ wire: 'Wire', ach: 'ACH', card: 'Credit card' }[method])}
                </label>
              ))}
            </fieldset>
          </fieldset>
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={disabled || busy || uncertain || !dirty || distributor.isActive !== true}
              className="me-distributor-admin-primary"
            >
              {t('Save purchase settings')}
            </button>
            {(dirty || uncertain) && (
              <button
                type="button"
                disabled={disabled || busy}
                onClick={discard}
                className="me-distributor-admin-secondary"
              >
                {t(distributor.isActive === true ? 'Discard and reload' : 'Discard changes')}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
